import React, { useCallback, useState } from "react";
import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button";
import Menu, { MenuProps } from "@mui/material/Menu";
import TranslateIcon from "@mui/icons-material/Translate";
import { Theme, Typography } from "@mui/material";

import { useConfig } from "../../../../hooks";
import { LanguageMenuItem } from "./LanguageMenuItem/LanguageMenuItem";
import { FormattedMessage } from "react-intl";

const StyledButton = styled(Button)(({ theme }) => ({
  height: "100px",
  textTransform: "none",
  borderRadius: "0px",
  width: "200px",
}));

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    {...props}
  />
))(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: "0px",
    "& .MuiMenu-list": {
      width: "200px",
      paddingTop: "1px",
      paddingBottom: "0px",
    },
  },
}));

export const LanguageMenu: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const cfg = useConfig();
  const language = cfg.config.language;

  const handleMenu = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const changeLanguage = useCallback(
    (language: string) => {
      cfg.setLanguage(language);
      handleClose();
    },
    [cfg, handleClose]
  );
  const styledButtonSX = (theme: Theme) => ({
    backgroundColor: open
      ? theme.palette.primary.main
      : theme.palette.common.white,
    "& .MuiButton-startIcon": {
      "& .MuiSvgIcon-root": {
        fontSize: "25px",
      },
    },
    "&:hover": {
      backgroundColor: theme.palette.common.white,
      "& .MuiButton-startIcon": {
        "& .MuiSvgIcon-root": {
          color: theme.palette.secondary.dark,
        },
      },
      "&>*": {
        color: theme.palette.secondary.dark,
      },
    },
  });

  const typographyAndIconSX = (theme: Theme) => ({
    color: open ? theme.palette.common.white : theme.palette.secondary.main,
  });
  return (
    <div>
      <StyledButton
        id="languageButton"
        aria-controls={open ? "languageMenu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        variant="contained"
        disableElevation
        onClick={handleMenu}
        sx={styledButtonSX}
        startIcon={<TranslateIcon sx={typographyAndIconSX} />}>
        <Typography variant="body1" color="secondary" sx={typographyAndIconSX}>
          <FormattedMessage id="language" />
        </Typography>
      </StyledButton>
      <StyledMenu
        id="languageMenu"
        MenuListProps={{
          "aria-labelledby": "languageButton",
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}>
        {["fi", "en-gb"].map((value) => (
          <LanguageMenuItem
            key={value}
            value={value}
            language={language}
            changeLanguage={changeLanguage}
          />
        ))}
      </StyledMenu>
    </div>
  );
};
